import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { budgetAPI, expenseAPI } from '../services/api';
import { useCategories } from '../contexts/CategoryContext';
import { relevantExpenseCategories } from './categoryRelevance';
import ExpenseForm from './ExpenseForm';

// Category names are user data now: they come from the database and are NOT
// translated — a self-chosen name has no translation.
function categoryLabel(expense) {
  return expense.category_name || expense.category;
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);
}

// Bookings keep their native sign: negative = Ausgabe, positive = Einnahme
// or Erstattung. Never force a sign here, the API already delivers the truth.
function amountTextClass(amount) {
  return (amount || 0) < 0 ? SEVERITY_TEXT.bad : SEVERITY_TEXT.ok;
}

function formatCurrencyCompact(amount) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(amount || 0);
}

function getPrevMonth(year, month) {
  if (month === 1) return { year: year - 1, month: 12 };
  return { year, month: month - 1 };
}

function getDaysLeftInMonth(year, month) {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;

  if (year < todayYear || (year === todayYear && month < todayMonth)) {
    return null;
  }

  if (year === todayYear && month === todayMonth) {
    const lastDay = new Date(year, month, 0).getDate();
    return lastDay - today.getDate();
  }

  return new Date(year, month, 0).getDate();
}

function severityFromRest(restPct) {
  if (restPct < 0) return 'bad';
  if (restPct < 0.1) return 'bad';
  if (restPct < 0.3) return 'warn';
  return 'ok';
}

const SEVERITY_TEXT = {
  ok: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-500 dark:text-amber-400',
  bad: 'text-rose-600 dark:text-rose-400',
};

const SEVERITY_FILL = {
  ok: 'bg-primary-500 dark:bg-primary-400',
  warn: 'bg-amber-500 dark:bg-amber-400',
  bad: 'bg-rose-500 dark:bg-rose-400',
};

function SectionSkeleton({ height = 'h-16' }) {
  return (
    <div className="miko-card overflow-hidden">
      <div className={`animate-pulse ${height} bg-primary-100 dark:bg-yoru-300/40 rounded`}></div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div className="miko-card p-6">
      <div className="animate-pulse space-y-3">
        <div className="h-3 bg-primary-100 dark:bg-yoru-300/40 rounded w-1/3"></div>
        <div className="h-10 bg-primary-100 dark:bg-yoru-300/40 rounded w-2/3"></div>
        <div className="h-2 bg-primary-100 dark:bg-yoru-300/40 rounded"></div>
      </div>
    </div>
  );
}

function CategoriesSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {[1, 2, 3, 4, 5, 6, 7].map(i => (
        <div key={i} className="miko-card p-3">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-primary-100 dark:bg-yoru-300/40 rounded w-2/3"></div>
            <div className="h-1.5 bg-primary-100 dark:bg-yoru-300/40 rounded"></div>
            <div className="h-3 bg-primary-100 dark:bg-yoru-300/40 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionError({ message }) {
  return (
    <div className="miko-card p-4 border-rose-300 dark:border-rose-700/60">
      <p className="text-sm text-rose-600 dark:text-rose-300/90">{message}</p>
    </div>
  );
}

function DashboardLanding() {
  const { t } = useTranslation();
  const { categories } = useCategories();


  const MONTH_NAMES = t('months.long', { returnObjects: true });

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  // Cache: { "<year>-<month>": { data, loading, error } }
  const [budgetCache, setBudgetCache] = useState({});

  const monthKey = `${year}-${month}`;
  const prev = getPrevMonth(year, month);
  const prevKey = `${prev.year}-${prev.month}`;

  const budgetEntry = budgetCache[monthKey];
  const budgetData = budgetEntry?.data ?? null;
  const budgetError = budgetEntry?.error ?? null;
  const budgetLoading = !budgetEntry || budgetEntry.loading === true;

  const prevBudgetEntry = budgetCache[prevKey];
  const prevBudgetData = prevBudgetEntry?.data ?? null;

  // Daily Transactions categories — the user's own expense categories, in
  // their own order. `key` IS the category code: budgetData.dailyTransactions
  // is keyed by code (see api.js), so lookups stay a plain index.
  //
  // Archived ones are kept for the month on display when they still carry
  // spending or a budget there — otherwise the month's numbers would not add
  // up against the hero total. They are marked and cannot be booked on.
  const DAILY_CATEGORIES = useMemo(
    () =>
      relevantExpenseCategories(categories, budgetData?.budget, budgetData?.actual).map(
        (category) => ({
          key: category.code,
          backendKey: category.code,
          label: category.name,
          icon: category.icon || '🏷️',
          isActive: category.is_active !== false,
        })
      ),
    [categories, budgetData]
  );

  const [recentExpenses, setRecentExpenses] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(null);

  // Same fetch as the budget analysis: it already carries the month's rows,
  // so this reads them out of the cache instead of asking a second time.
  const monthExpenses = budgetData?.expenses || [];
  const monthExpensesLoading = budgetLoading;
  const monthExpensesError = budgetError;

  const [recentExpanded, setRecentExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [formExpense, setFormExpense] = useState(null);
  const [formPrefillCategory, setFormPrefillCategory] = useState(null);

  const fetchRecent = async () => {
    try {
      setRecentLoading(true);
      setRecentError(null);
      const resp = await expenseAPI.getRecentExpenses(5);
      setRecentExpenses(resp.data || []);
    } catch (err) {
      console.error('DashboardLanding recent error:', err);
      setRecentError(err.message || t('dashboardLanding.loadFailed'));
    } finally {
      setRecentLoading(false);
    }
  };

  const fetchMonthBudget = async (y, m) => {
    const key = `${y}-${m}`;
    let skip = false;
    setBudgetCache(curr => {
      const existing = curr[key];
      if (existing?.data && !existing.error) {
        skip = true;
        return curr;
      }
      return { ...curr, [key]: { ...(existing || {}), loading: true, error: null } };
    });
    if (skip) return;
    try {
      const resp = await budgetAPI.getBudgetVsActualAnalysis(y, m);
      setBudgetCache(curr => ({ ...curr, [key]: { data: resp.data, loading: false, error: null } }));
    } catch (err) {
      console.error('DashboardLanding budget error:', err);
      setBudgetCache(curr => ({
        ...curr,
        [key]: { ...(curr[key] || {}), loading: false, error: err.message || t('dashboardLanding.loadFailed') },
      }));
    }
  };

  const refreshAll = ({ invalidate = false } = {}) => {
    if (invalidate) {
      setBudgetCache(curr => {
        const next = { ...curr };
        delete next[monthKey];
        delete next[prevKey];
        return next;
      });
    }
    fetchRecent();
    fetchMonthBudget(year, month);
    fetchMonthBudget(prev.year, prev.month);
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // Aggregates for hero card
  const totals = useMemo(() => {
    if (!budgetData) return null;
    const curDt = budgetData.budget?.dailyTransactions || {};
    const curActDt = budgetData.actual?.dailyTransactions || {};
    const prevActDt = prevBudgetData?.actual?.dailyTransactions || {};

    let totalBudget = 0;
    let totalActual = 0;
    let totalPrevActual = 0;

    DAILY_CATEGORIES.forEach(c => {
      totalBudget += Math.abs(curDt[c.key] || 0);
      totalActual += -(curActDt[c.key] || 0);
      totalPrevActual += -(prevActDt[c.key] || 0);
    });

    const rest = totalBudget - totalActual;
    const usedPct = totalBudget > 0 ? totalActual / totalBudget : 0;
    const restPct = totalBudget > 0 ? rest / totalBudget : 0;
    const deltaVsPrev = totalActual - totalPrevActual;
    const deltaPct = totalPrevActual > 0 ? (deltaVsPrev / totalPrevActual) : 0;

    return { totalBudget, totalActual, totalPrevActual, rest, usedPct, restPct, deltaVsPrev, deltaPct };
  }, [budgetData, prevBudgetData, DAILY_CATEGORIES]);

  const categoryRows = useMemo(() => {
    if (!budgetData) return [];
    const curDt = budgetData.budget?.dailyTransactions || {};
    const curActDt = budgetData.actual?.dailyTransactions || {};
    const prevActDt = prevBudgetData?.actual?.dailyTransactions || {};

    return DAILY_CATEGORIES.map(c => {
      const budget = Math.abs(curDt[c.key] || 0);
      const actual = -(curActDt[c.key] || 0);
      const prevActual = -(prevActDt[c.key] || 0);
      const rest = budget - actual;
      const usedPct = budget > 0 ? actual / budget : 0;
      const restPct = budget > 0 ? rest / budget : 0;
      const delta = actual - prevActual;
      return { ...c, budget, actual, prevActual, rest, usedPct, restPct, delta };
    });
  }, [budgetData, prevBudgetData, DAILY_CATEGORIES]);

  // For each category, build the list of this-month expenses (read-only).
  // monthExpenses is already scoped to (year, month) via /expenses/by-month,
  // so no client-side date filter is needed.
  const expensesByCategory = useMemo(() => {
    const map = {};
    DAILY_CATEGORIES.forEach(c => { map[c.key] = []; });
    if (!monthExpenses || monthExpenses.length === 0) return map;

    monthExpenses.forEach(exp => {
      const cat = DAILY_CATEGORIES.find(c => c.backendKey === exp.category);
      if (cat) map[cat.key].push(exp);
    });

    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => new Date(b.date) - new Date(a.date));
    });
    return map;
  }, [monthExpenses, DAILY_CATEGORIES]);

  const daysLeft = getDaysLeftInMonth(year, month);

  const handleAddExpense = (prefillCategory = null) => {
    setFormExpense(null);
    setFormPrefillCategory(prefillCategory);
    setFormOpen(true);
  };

  const handleFormSubmit = async (data) => {
    try {
      await expenseAPI.createExpense(data);
      setFormOpen(false);
      setFormExpense(null);
      setFormPrefillCategory(null);
      refreshAll({ invalidate: true });
    } catch (err) {
      console.error('Save expense error:', err);
      throw new Error(t('dashboardLanding.saveFailed'));
    }
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const goPrevMonth = () => {
    const p = getPrevMonth(year, month);
    setYear(p.year);
    setMonth(p.month);
  };
  const goNextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };

  return (
    <div className="space-y-4">
      {/* Topbar — always rendered, no gating */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={goPrevMonth}
            className="text-primary-600 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-yoru-300/40 rounded-full w-8 h-8 flex items-center justify-center"
            aria-label={t('dashboardLanding.prevMonth')}
          >‹</button>
          <h1 className="text-xl md:text-2xl font-bold miko-heading">
            {`${t('dashboardLanding.titlePrefix')}${MONTH_NAMES[month - 1]} ${year}`}
          </h1>
          <button
            onClick={goNextMonth}
            className="text-primary-600 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-yoru-300/40 rounded-full w-8 h-8 flex items-center justify-center"
            aria-label={t('dashboardLanding.nextMonth')}
          >›</button>
        </div>
        <button
          onClick={() => handleAddExpense()}
          className="bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-semibold px-4 py-2 rounded-full shadow-md text-sm flex items-center gap-1"
        >
          <span className="text-lg leading-none">+</span> {t('dashboardLanding.addExpense')}
        </button>
      </div>

      {/* Letzte Buchungen — collapsible, default closed */}
      {recentLoading ? (
        <SectionSkeleton height="h-12" />
      ) : recentError ? (
        <SectionError message={t('dashboardLanding.recentLoadError', { error: recentError })} />
      ) : (
        <div className="miko-card overflow-hidden">
          <button
            onClick={() => setRecentExpanded(!recentExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-primary-50 dark:hover:bg-yoru-300/40 transition-colors"
          >
            <span className="font-semibold miko-heading flex items-center gap-2">
              {t('dashboardLanding.recent')}
              <span className="text-xs miko-text-muted font-normal">({recentExpenses.length})</span>
            </span>
            <svg className={`w-4 h-4 transition-transform ${recentExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {recentExpanded && (
            <div className="px-4 pb-4 space-y-2">
              {recentExpenses.length === 0 ? (
                <p className="text-sm miko-text-muted">{t('dashboardLanding.noEntries')}</p>
              ) : recentExpenses.map(exp => (
                <div key={exp.id} className="flex justify-between items-center py-2 border-b border-primary-100 dark:border-accent-500/20 last:border-0 text-sm">
                  <div className="flex flex-col">
                    <span className="miko-text">
                      {new Date(exp.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · {exp.description}
                    </span>
                    <span className="text-xs miko-text-muted">{categoryLabel(exp)}</span>
                  </div>
                  <span className={`font-semibold ${amountTextClass(exp.amount)}`}>
                    {formatCurrency(exp.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hero — Ausgaben */}
      {budgetLoading ? (
        <HeroSkeleton />
      ) : budgetError ? (
        <SectionError message={t('dashboardLanding.expensesLoadError', { error: budgetError })} />
      ) : !totals ? null : (
        <div className="bg-gradient-to-br from-primary-500 via-primary-400 to-accent-500 dark:from-primary-700 dark:via-primary-600 dark:to-accent-600 text-white rounded-miko p-6 shadow-lg">
          <div className="text-xs uppercase tracking-widest opacity-90 mb-1">
            {t('dashboardLanding.expensesHeading', { month: `${MONTH_NAMES[month - 1]} ${year}` })}
          </div>
          <div className="text-3xl md:text-4xl font-extrabold leading-tight">
            {formatCurrencyCompact(totals.totalActual)}
            <span className="opacity-60 font-medium mx-2">/</span>
            <span className="text-xl md:text-2xl font-semibold opacity-90">{formatCurrencyCompact(totals.totalBudget)}</span>
          </div>
          <div className="mt-3 h-2 bg-white/25 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${Math.max(0, Math.min(totals.usedPct * 100, 100))}%` }}
            ></div>
          </div>
          <div className="mt-2 text-xs md:text-sm opacity-95 flex flex-wrap gap-x-3 gap-y-1">
            <span>{Math.round(totals.usedPct * 100)}{t('dashboardLanding.percentUsed')}</span>
            <span>·</span>
            <span>{daysLeft === null ? t('dashboardLanding.monthDone') : `${daysLeft} ${t('dashboardLanding.daysLeft')}`}</span>
            <span>·</span>
            <span>
              {totals.totalActual < 0
                ? t('dashboardLanding.refunded', { amount: formatCurrencyCompact(Math.abs(totals.totalActual)) })
                : totals.rest >= 0
                  ? t('dashboardLanding.remaining', { amount: formatCurrencyCompact(totals.rest) })
                  : t('dashboardLanding.overspent', { amount: formatCurrencyCompact(Math.abs(totals.rest)) })}
            </span>
            {prevBudgetData && (
              <>
                <span>·</span>
                <span>
                  {totals.deltaVsPrev > 0 ? '▲' : (totals.deltaVsPrev < 0 ? '▼' : '·')} {Math.abs(totals.deltaPct * 100).toFixed(0)}% {t('dashboardLanding.vsLastMonth', { month: MONTH_NAMES[getPrevMonth(year, month).month - 1] })}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Kategorien */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-primary-700 dark:text-primary-300/80 font-semibold mb-2">
          {t('dashboardLanding.categories')}
        </h3>
        {budgetLoading ? (
          <CategoriesSkeleton />
        ) : budgetError ? null : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categoryRows.map(row => {
              const sev = severityFromRest(row.restPct);
              const expanded = !!expandedCategories[row.key];
              const expenses = expensesByCategory[row.key] || [];
              const deltaArrow = row.delta > 0 ? '▲' : (row.delta < 0 ? '▼' : '·');
              const deltaAbs = Math.abs(row.delta);

              return (
                <div key={row.key} className="miko-card overflow-hidden">
                  <button
                    onClick={() => toggleCategory(row.key)}
                    className="w-full text-left p-3 hover:bg-primary-50 dark:hover:bg-yoru-300/40 transition-colors"
                  >
                    <div className="flex justify-between items-baseline">
                      <span className="font-semibold miko-text flex items-center gap-2">
                        <span>{row.icon}</span>
                        <span>{row.label}</span>
                        {!row.isActive && (
                          <span className="text-xs rounded bg-amber-100 px-2 py-0.5 font-normal text-amber-900 dark:bg-amber-900/50 dark:text-amber-200">
                            {t('categoryManagement.archived')}
                          </span>
                        )}
                        <svg className={`w-3 h-3 miko-text-muted transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                      <span className={`font-bold ${SEVERITY_TEXT[sev]}`}>
                        {formatCurrencyCompact(row.actual)} / {formatCurrencyCompact(row.budget)}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-primary-100 dark:bg-yoru-300/40 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${SEVERITY_FILL[sev]}`}
                        style={{ width: `${Math.max(0, Math.min(row.usedPct * 100, 100))}%` }}
                      ></div>
                    </div>
                    <div className="mt-1 flex justify-between text-xs miko-text-muted">
                      <span>
                        {row.actual < 0
                          ? t('dashboardLanding.refunded', { amount: formatCurrencyCompact(Math.abs(row.actual)) })
                          : `${row.rest >= 0 ? '▼' : '▲'} ${formatCurrencyCompact(Math.abs(row.rest))}`}
                      </span>
                      {prevBudgetData && (
                        <span>
                          {t('dashboardLanding.vsPrev')} {deltaArrow} {formatCurrencyCompact(deltaAbs)}
                        </span>
                      )}
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-primary-100 dark:border-accent-500/20 bg-primary-50/40 dark:bg-yoru-300/20 p-3 relative">
                      {/* No booking shortcut for an archived category — the
                          API rejects the write with invalid_expense_category,
                          so offering it would only produce an error. */}
                      {row.isActive && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAddExpense(row.backendKey); }}
                          className="absolute top-2 right-2 text-xs text-primary-600 dark:text-primary-300 hover:bg-white/80 dark:hover:bg-yoru-500/60 rounded-full px-2 py-1 border border-primary-200 dark:border-accent-500/30 flex items-center gap-1"
                          title={t('dashboardLanding.newEntryIn', { category: row.label })}
                        >
                          ✏️ <span className="hidden sm:inline">{t('dashboardLanding.book')}</span>
                        </button>
                      )}
                      <div className="text-xs miko-text-muted mb-2">
                        {t('dashboardLanding.bookings', { month: MONTH_NAMES[month - 1] })} ({expenses.length})
                      </div>
                      {monthExpensesLoading ? (
                        <p className="text-xs miko-text-muted italic animate-pulse">{t('dashboardLanding.loadingBookings')}</p>
                      ) : monthExpensesError ? (
                        <p className="text-xs text-rose-600 dark:text-rose-400">{t('dashboardLanding.bookingsLoadError', { error: monthExpensesError })}</p>
                      ) : expenses.length === 0 ? (
                        <p className="text-xs miko-text-muted italic">{t('dashboardLanding.noBookings')}</p>
                      ) : (
                        <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                          {expenses.map(exp => (
                            <div key={exp.id} className="flex justify-between items-baseline text-xs py-1 border-b border-primary-100/60 dark:border-accent-500/10 last:border-0">
                              <span className="miko-text">
                                {new Date(exp.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · {exp.description}
                              </span>
                              <span className={`font-semibold whitespace-nowrap ml-2 ${amountTextClass(exp.amount)}`}>
                                {formatCurrency(exp.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ExpenseForm modal */}
      {formOpen && (
        <ExpenseForm
          expense={formExpense}
          prefillCategory={formPrefillCategory}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setFormOpen(false);
            setFormExpense(null);
            setFormPrefillCategory(null);
          }}
        />
      )}
    </div>
  );
}

export default DashboardLanding;
